import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { flatten } from 'ramda';
import { FormattedMessage } from 'react-intl';

import CurrencySymbol from 'components/ui/CurrencySymbol';
import Modal from 'components/ui/Modal';

import { WRITE_OFF_REASONS } from 'constants/reasons';
import { productsShape } from 'constants/propTypes';
import Panel from '../Panel';
import { RowHeader } from '../Payments.styles';
import FeeItem from './FeeItem';

import { CancelRefund, ConfirmRefund, AmountToBeRefunded } from '../BookingFees/BookingFees.styles';

const OtherFees = ({
  onRefund,
  onCancel,
  onConfirm,
  isRefunding,
  isCancelling,
  isConfirming,
  otherOutboundFees,
  otherInboundFees,
  intl: { formatMessage },
  toBeRefunded,
  disabled,
  disabledTooltipMessageId
}) => {
  const [feesSelected, setFeesSelected] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [reasonCode, setReasonCode] = useState('');
  const [additionalExplanation, setAdditionalExplanation] = useState('');
  const [label, setLabel] = useState('');

  const loading = [isRefunding, isCancelling, isConfirming].some(Boolean);
  const reason = reasonCode && WRITE_OFF_REASONS.find(({ code }) => code === reasonCode);
  const hasOptions = flatten(Object.values(feesSelected)).length > 0;

  const showAdditionalExplanation = Boolean(reason && reason.requiresAdditional);

  const startingWithWhitespaceUnlimited = /^\s*$/;
  const canSubmitExplanation = showAdditionalExplanation ? !additionalExplanation.match(startingWithWhitespaceUnlimited) : true;

  const canCalculateRefund = Boolean(!loading && reasonCode && canSubmitExplanation);

  const handleSubmit = async (event) => {
    event.preventDefault();

    await onRefund({
      itemRefs: flatten(Object.values(feesSelected)),
      reasonCode,
      additionalExplanation,
      label
    });

    setShowModal(true);
    setFeesSelected({});
    setReasonCode('');
    setLabel('');
    setAdditionalExplanation('');
  };

  const handleConfirm = async () => {
    await onConfirm();
    setShowModal(false);
  };

  const handleCancel = async () => {
    await onCancel();
    setShowModal(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Panel title="paymentsTab.otherFees">
        <>
          <RowHeader>
            <FormattedMessage id="journeySummary.outbound" />
          </RowHeader>
          {otherOutboundFees.length > 0 ? (
            otherOutboundFees.map((otherFee, index) => (
              <FeeItem
                key={`${index}_${otherFee?.itemRef}`}
                feesSelected={feesSelected}
                setFeesSelected={setFeesSelected}
                fee={otherFee}
                reasonCode={reasonCode}
                setReasonCode={setReasonCode}
                label={label}
                setLabel={setLabel}
                hasOptions={hasOptions}
                showAdditionalExplanation={showAdditionalExplanation}
                additionalExplanation={additionalExplanation}
                setAdditionalExplanation={setAdditionalExplanation}
                canCalculateRefund={canCalculateRefund}
                loading={loading}
                formatMessage={formatMessage}
                direction="outbound"
                disabled={disabled}
                disabledTooltipMessageId={disabledTooltipMessageId}
              />
            ))
          ) : (
            <FormattedMessage id="paymentsTab.none" />
          )}
        </>
        <>
          <RowHeader>
            <FormattedMessage id="journeySummary.inbound" />
          </RowHeader>
          {otherInboundFees.length > 0 ? (
            otherInboundFees.map((otherFee, index) => (
              <FeeItem
                key={`${index}_${otherFee?.itemRef}`}
                fee={otherFee}
                feesSelected={feesSelected}
                setFeesSelected={setFeesSelected}
                reasonCode={reasonCode}
                setReasonCode={setReasonCode}
                label={label}
                setLabel={setLabel}
                hasOptions={hasOptions}
                showAdditionalExplanation={showAdditionalExplanation}
                additionalExplanation={additionalExplanation}
                setAdditionalExplanation={setAdditionalExplanation}
                canCalculateRefund={canCalculateRefund}
                loading={loading}
                formatMessage={formatMessage}
                direction="inbound"
                disabled={disabled}
                disabledTooltipMessageId={disabledTooltipMessageId}
              />
            ))
          ) : (
            <FormattedMessage id="paymentsTab.none" />
          )}
        </>
      </Panel>
      <Modal
        title={formatMessage({ id: 'paymentsTab.modalTitle' })}
        isOpen={showModal}
        bottomRow={
          <>
            <ConfirmRefund data-cy="other-fees-confirm" onClick={handleConfirm} disabled={loading} loading={isConfirming}>
              {formatMessage({ id: 'paymentsTab.confirmRefund' })}
            </ConfirmRefund>
            <CancelRefund data-cy="other-fees-cancel" onClick={handleCancel} disabled={loading} loading={isCancelling}>
              {formatMessage({ id: 'paymentsTab.cancelRefund' })}
            </CancelRefund>
          </>
        }
      >
        <h3>
          {formatMessage({ id: 'paymentsTab.amountToBeRefunded' })}:
          <AmountToBeRefunded>
            <CurrencySymbol currencyCode={toBeRefunded.currency} />
            {Math.abs(toBeRefunded.amount)}
          </AmountToBeRefunded>
        </h3>
      </Modal>
    </form>
  );
};

OtherFees.propTypes = {
  toBeRefunded: PropTypes.shape({
    amount: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired
  }),
  otherInboundFees: productsShape.isRequired,
  otherOutboundFees: productsShape.isRequired,
  onRefund: PropTypes.func,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  isRefunding: PropTypes.bool,
  isCancelling: PropTypes.bool,
  isConfirming: PropTypes.bool,
  intl: PropTypes.shape({ formatMessage: PropTypes.func }),
  disabled: PropTypes.bool,
  disabledTooltipMessageId: PropTypes.string
};
export default OtherFees;
